import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AeroMonthTabs from "../../components/aero/AeroMonthTabs";
import AeroFlightCard from "../../components/aero/AeroFlightCard";
import AeroLoadingSpinner from "../../components/aero/AeroLoadingSpinner";
import {
    searchDarwinFlights,
    buildDarwinSearchRequest,
    AERO_AIRPORTS,
} from "../../services/darwinApi";
import {
    DarwinTrip,
    DarwinFareClass,
    DarwinFare,
    DarwinPtcFare,
    AeroSelectedFlight,
    AeroCheckoutState,
} from "../../types/darwin";
import { useMockMode } from "../../hooks/useMockMode";
import { buildMockAeroTrips } from "../../services/mockOffers";

// Aero gradient logo — 32x42 rounded rectangle with larger corner radius
const AeroLogoSmall: React.FC = () => (
    <div
        className="flex flex-col overflow-hidden"
        style={{ width: 32, height: 42, borderRadius: 14 }}
    >
        {["#a40000", "#c51600", "#e42c01", "#fb6900", "#fc9301", "#fdc002"].map(
            (c) => (
                <div
                    key={c}
                    className="flex-1"
                    style={{ backgroundColor: c }}
                />
            ),
        )}
    </div>
);

const BackArrow: React.FC = () => (
    <svg width="20" height="20" viewBox="0 0 48 48" fill="currentColor">
        <path d="M38 22.5H14.83l10.59-10.58L24 10.5l-14 14 14 14 1.41-1.41L14.83 26.5H38v-4Z" />
    </svg>
);

const RightArrow: React.FC = () => (
    <svg width="20" height="20" viewBox="0 0 48 48" fill="currentColor">
        <path
            fillRule="evenodd"
            d="M41 24 28 40h-3.1l11.28-14.58H9v-2.84h27.18L24.9 8h3.12L41 24Z"
            clipRule="evenodd"
        />
    </svg>
);

const PersonIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 48 48" fill="currentColor">
        <path d="M24 24c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8Zm0 4c5.33 0 16 2.67 16 8v4H8v-4c0-5.33 10.67-8 16-8Z" />
    </svg>
);

const CalendarIcon: React.FC = () => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 48 48"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
    >
        <rect x="8" y="10" width="32" height="30" rx="4" />
        <path d="M16 6v8M32 6v8M8 20h32" />
    </svg>
);

type FlowStep = "departure" | "return";

const AeroFlightResults: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const mockMode = useMockMode();

    const originParam = searchParams.get("origin") ?? "MCO";
    const destinationParam = searchParams.get("destination") ?? "JFK";
    const adultsParam = parseInt(searchParams.get("adults") ?? "1", 10);
    const infantsParam = parseInt(searchParams.get("infants") ?? "0", 10);
    const tripTypeParam =
        (searchParams.get("tripType") as "roundtrip" | "oneway") ?? "roundtrip";

    const [step, setStep] = useState<FlowStep>("departure");
    const [selectedDeparture, setSelectedDeparture] =
        useState<AeroSelectedFlight | null>(null);
    const [expandedTripUid, setExpandedTripUid] = useState<string | null>(null);

    const now = new Date();
    const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
    const [selectedYear, setSelectedYear] = useState(now.getFullYear());

    const [trips, setTrips] = useState<DarwinTrip[]>([]);
    const [sessionId, setSessionId] = useState<string | undefined>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Prevent duplicate API calls
    const fetchRef = useRef(0);

    const currentOrigin = step === "departure" ? originParam : destinationParam;
    const currentDestination =
        step === "departure" ? destinationParam : originParam;
    const originAirport = AERO_AIRPORTS[currentOrigin];

    useEffect(() => {
        const fetchId = ++fetchRef.current;

        const fetchFlights = async () => {
            setLoading(true);
            setError(null);
            setExpandedTripUid(null);

            try {
                const firstDay = new Date(selectedYear, selectedMonth, 1);
                const lastDay = new Date(selectedYear, selectedMonth + 1, 0);
                const pad = (n: number) => String(n).padStart(2, "0");
                const toDateStr = (d: Date) =>
                    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

                // Mock mode skips Darwin and synthesizes 3-5 trips on a date
                // mid-month so the calendar tab feels populated. No network
                // call, no session ID — Aero's existing UI components don't
                // care, and mock-aero on the backend will accept whatever
                // offer the user picks at checkout time.
                if (mockMode) {
                    const mid = new Date(
                        selectedYear,
                        selectedMonth,
                        Math.min(15, lastDay.getDate()),
                    );
                    const trips = buildMockAeroTrips({
                        origin: currentOrigin,
                        destination: currentDestination,
                        departureDate: toDateStr(mid),
                        adults: adultsParam,
                    });
                    if (fetchId === fetchRef.current) {
                        let filteredTrips = trips;
                        if (step === "return" && selectedDeparture) {
                            const depDateStr = selectedDeparture.trip.tripFlight.departureDate.substring(0, 10);
                            filteredTrips = filteredTrips.filter((t) => t.tripFlight.departureDate.substring(0, 10) > depDateStr);
                        }
                        setSessionId(undefined);
                        setTrips(filteredTrips);
                    }
                    return;
                }

                const request = buildDarwinSearchRequest(
                    currentOrigin,
                    currentDestination,
                    toDateStr(firstDay),
                    toDateStr(lastDay),
                    adultsParam,
                );

                const response = await searchDarwinFlights(request);

                // Only update state if this is still the latest request
                if (fetchId === fetchRef.current) {
                    if (response.sessionId) {
                        setSessionId(response.sessionId);
                    }
                    let filteredTrips = response.trips ?? [];
                    // For return step, filter out flights before the departure date
                    if (step === "return" && selectedDeparture) {
                        const depDateStr =
                            selectedDeparture.trip.tripFlight.departureDate.substring(
                                0,
                                10,
                            );
                        filteredTrips = filteredTrips.filter((t) => {
                            const tripDate =
                                t.tripFlight.departureDate.substring(0, 10);
                            return tripDate > depDateStr;
                        });
                    }
                    setTrips(filteredTrips);
                }
            } catch (err) {
                console.error("Darwin search error:", err);
                if (fetchId === fetchRef.current) {
                    setError("Unable to load flights. Please try again.");
                    setTrips([]);
                }
            } finally {
                if (fetchId === fetchRef.current) {
                    setLoading(false);
                }
            }
        };

        fetchFlights();
    }, [
        currentOrigin,
        currentDestination,
        selectedMonth,
        selectedYear,
        adultsParam,
        mockMode,
        step,
        selectedDeparture,
    ]);

    const handleMonthChange = (month: number, year: number) => {
        setSelectedMonth(month);
        setSelectedYear(year);
    };

    const handleCardClick = (tripUid: string) => {
        setExpandedTripUid((prev) => (prev === tripUid ? null : tripUid));
    };

    const handleFareSelect = (
        trip: DarwinTrip,
        fareClass: DarwinFareClass,
        fare: DarwinFare,
        ptcFare: DarwinPtcFare,
    ) => {
        const selected: AeroSelectedFlight = { trip, fareClass, fare, ptcFare };

        if (step === "departure") {
            setSelectedDeparture(selected);
            if (tripTypeParam === "oneway") {
                navigate("/aero/checkout", {
                    state: {
                        departure: selected,
                        returnFlight: null,
                        adults: adultsParam,
                        infants: infantsParam,
                        tripType: "oneway",
                        sessionId,
                    } as AeroCheckoutState,
                });
            } else {
                setStep("return");
                setExpandedTripUid(null);
                // Set month tab to the departure date's month so return flights start from there
                const depDate = new Date(
                    selected.trip.tripFlight.departureDate,
                );
                setSelectedMonth(depDate.getMonth());
                setSelectedYear(depDate.getFullYear());
            }
        } else {
            navigate("/aero/checkout", {
                state: {
                    departure: selectedDeparture!,
                    returnFlight: selected,
                    adults: adultsParam,
                    infants: infantsParam,
                    tripType: "roundtrip",
                    sessionId,
                } as AeroCheckoutState,
            });
        }
    };

    // Left panel heading changes based on state
    const headingText = expandedTripUid
        ? "Select your fare"
        : step === "departure"
          ? `When would you like to depart from ${originAirport?.city ?? currentOrigin}?`
          : `When would you like to return from ${AERO_AIRPORTS[currentOrigin]?.city ?? currentOrigin}?`;

    return (
        <div
            className="flex h-screen overflow-hidden"
            style={{
                fontFamily:
                    '"Haas Grot Disp Web", "Haas Grot Text Web", "Helvetica Neue", Helvetica, Arial, sans-serif',
            }}
        >
            {/* ── Left panel — black background ── */}
            {/* Aero's layout: 80px page margin + 659px panel = ~42% of viewport */}
            <div
                className="relative flex-shrink-0"
                style={{ width: "42%", backgroundColor: "#000" }}
            >
                {/* Logo — absolute top-left at 24px from edges */}
                <a
                    href="/aero"
                    style={{ position: "absolute", left: 24, top: 24 }}
                >
                    <AeroLogoSmall />
                </a>

                {/* Heading — horizontally centered in the panel, near top */}
                <div
                    className="flex items-start justify-center"
                    style={{
                        paddingTop: 80,
                        paddingLeft: 80,
                        paddingRight: 40,
                    }}
                >
                    <h1
                        style={{
                            fontSize: 40,
                            fontWeight: 400,
                            lineHeight: "48px",
                            letterSpacing: "-0.16px",
                            color: "#fff",
                            margin: 0,
                            maxWidth: 480,
                        }}
                    >
                        {headingText}
                    </h1>
                </div>
            </div>

            {/* ── Right panel (62%) — white background, scrollable ── */}
            <div
                className="flex-1 bg-white overflow-y-auto"
                style={{ padding: 24 }}
            >
                {/* Content constrained to 480px, centered */}
                <div
                    style={{ maxWidth: 480, margin: "0 auto", paddingTop: 56 }}
                >
                    {/* Top bar: back arrow + route summary */}
                    <div className="flex items-center">
                        <button
                            onClick={() => {
                                if (step === "return") {
                                    setStep("departure");
                                    setSelectedDeparture(null);
                                } else {
                                    navigate("/aero");
                                }
                            }}
                            className="flex items-center justify-center hover:opacity-70 transition-opacity"
                            style={{
                                background: "none",
                                border: "none",
                                padding: 0,
                                cursor: "pointer",
                                color: "#000",
                            }}
                        >
                            <BackArrow />
                        </button>

                        {/* Route summary — right aligned */}
                        <div
                            className="flex items-center gap-2 ml-auto"
                            style={{ fontSize: 14, color: "#949494" }}
                        >
                            <span>{originParam}</span>
                            <span>→</span>
                            <span>{destinationParam}</span>
                            <span style={{ margin: "0 4px" }}>|</span>
                            <span>
                                {tripTypeParam === "roundtrip"
                                    ? "Round Trip"
                                    : "One Way"}
                            </span>
                            <span style={{ margin: "0 4px" }}>|</span>
                            <span className="flex items-center gap-1">
                                <PersonIcon />
                                {adultsParam}
                            </span>
                            {infantsParam > 0 && (
                                <span className="flex items-center gap-1">
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 48 48"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M25.6 15.67c-3.4 1.34-6.6-1.65-6.6-5.18h4c0 1.48 1.04 1.5 1.15 1.46.09-.04.54-.27.81-1.42.19-.8.26-1.94.07-3.51L24 7c-8.42 0-12.8 3.76-14.98 7.9a17.4 17.4 0 0 0-1.74 5.4 5 5 0 0 0 .2 9.46c.3 1.4.78 2.89 1.54 4.33C11.2 38.24 15.58 42 24 42c8.42 0 12.8-3.76 14.98-7.9a17.06 17.06 0 0 0 1.55-4.34 5 5 0 0 0 .2-9.45 17.41 17.41 0 0 0-1.75-5.4c-1.69-3.22-4.7-6.2-9.88-7.38.1 1.5.02 2.81-.25 3.92-.47 2.02-1.59 3.57-3.24 4.22ZM15.18 29.1c.15.83.42 1.73.87 2.6C17.2 33.97 19.52 36 24 36s6.8-2.04 7.96-4.3c.45-.87.72-1.77.87-2.6H15.17ZM20 22.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0ZM30.5 25a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    {infantsParam}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Main content */}
                    <div>
                        {/* "Departure" / "Return" heading */}
                        <h2
                            style={{
                                fontSize: 24,
                                fontWeight: 400,
                                lineHeight: "28px",
                                color: "#000",
                                margin: "0 0 16px 0",
                            }}
                        >
                            {step === "departure" ? "Departure" : "Return"}
                        </h2>

                        {/* Month tabs — hidden when a card is expanded (fare selection mode) */}
                        {!expandedTripUid && (
                            <AeroMonthTabs
                                selectedMonth={selectedMonth}
                                selectedYear={selectedYear}
                                onMonthChange={handleMonthChange}
                            />
                        )}

                        {/* Flight cards */}
                        <div style={{ marginTop: 24 }}>
                            {loading && <AeroLoadingSpinner />}

                            {!loading && error && (
                                <div
                                    style={{
                                        padding: "48px 0",
                                        textAlign: "center",
                                    }}
                                >
                                    <p
                                        style={{
                                            fontSize: 14,
                                            color: "#949494",
                                        }}
                                    >
                                        {error}
                                    </p>
                                    <button
                                        onClick={() => {
                                            fetchRef.current++;
                                            setLoading(true);
                                        }}
                                        style={{
                                            marginTop: 16,
                                            padding: "8px 16px",
                                            backgroundColor: "#fb6900",
                                            color: "#fff",
                                            fontSize: 14,
                                            fontWeight: 500,
                                            borderRadius: 8,
                                            border: "none",
                                            cursor: "pointer",
                                        }}
                                    >
                                        Try again
                                    </button>
                                </div>
                            )}

                            {!loading && !error && trips.length === 0 && (
                                <div
                                    style={{
                                        padding: "48px 0",
                                        textAlign: "center",
                                    }}
                                >
                                    <p
                                        style={{
                                            fontSize: 14,
                                            color: "#949494",
                                            fontWeight: 500,
                                        }}
                                    >
                                        No flights available this month
                                    </p>
                                    <p
                                        style={{
                                            fontSize: 12,
                                            color: "#c6c6c6",
                                            marginTop: 4,
                                        }}
                                    >
                                        Try selecting a different month
                                    </p>
                                </div>
                            )}

                            {/* When a card is expanded, only show that one card */}
                            {!loading &&
                                expandedTripUid &&
                                trips
                                    .filter(
                                        (t) =>
                                            t.availabilityTripUid ===
                                            expandedTripUid,
                                    )
                                    .map((trip) => (
                                        <AeroFlightCard
                                            key={trip.availabilityTripUid}
                                            trip={trip}
                                            isExpanded
                                            onClick={() =>
                                                handleCardClick(
                                                    trip.availabilityTripUid,
                                                )
                                            }
                                            onSelect={(
                                                fareClass,
                                                fare,
                                                ptcFare,
                                            ) =>
                                                handleFareSelect(
                                                    trip,
                                                    fareClass,
                                                    fare,
                                                    ptcFare,
                                                )
                                            }
                                        />
                                    ))}

                            {/* When no card is expanded, show all cards */}
                            {!loading &&
                                !expandedTripUid &&
                                trips.map((trip) => (
                                    <AeroFlightCard
                                        key={trip.availabilityTripUid}
                                        trip={trip}
                                        isExpanded={false}
                                        onClick={() =>
                                            handleCardClick(
                                                trip.availabilityTripUid,
                                            )
                                        }
                                        onSelect={(fareClass, fare, ptcFare) =>
                                            handleFareSelect(
                                                trip,
                                                fareClass,
                                                fare,
                                                ptcFare,
                                            )
                                        }
                                    />
                                ))}
                        </div>

                        {/* Bottom CTAs — hidden when fare selection is active */}
                        {!loading && !expandedTripUid && (
                            <div style={{ marginTop: 24 }}>
                                {/* "Want to fly a different day?" */}
                                <div
                                    className="flex items-center cursor-pointer"
                                    style={{
                                        padding: "16px 20px",
                                        border: "1px solid #e9e9e9",
                                        borderRadius: 8,
                                        gap: 12,
                                    }}
                                >
                                    <div
                                        style={{
                                            color: "#949494",
                                            flexShrink: 0,
                                        }}
                                    >
                                        <CalendarIcon />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <p
                                            style={{
                                                fontSize: 14,
                                                fontWeight: 500,
                                                color: "#000",
                                                margin: 0,
                                            }}
                                        >
                                            Want to fly a different day?
                                        </p>
                                        <p
                                            style={{
                                                fontSize: 12,
                                                color: "#949494",
                                                margin: "2px 0 0",
                                            }}
                                        >
                                            Sign up to be notified of additional
                                            flight dates.
                                        </p>
                                    </div>
                                    <div
                                        style={{ color: "#000", flexShrink: 0 }}
                                    >
                                        <RightArrow />
                                    </div>
                                </div>

                                {/* "Skip, buy one way trip" — only on return step */}
                                {step === "return" && (
                                    <button
                                        onClick={() => {
                                            navigate("/aero/checkout", {
                                                state: {
                                                    departure:
                                                        selectedDeparture!,
                                                    returnFlight: null,
                                                    adults: adultsParam,
                                                    infants: infantsParam,
                                                    tripType: "oneway",
                                                    sessionId,
                                                } as AeroCheckoutState,
                                            });
                                        }}
                                        className="flex items-center gap-2"
                                        style={{
                                            marginTop: 16,
                                            padding: "16px 20px",
                                            border: "1px solid #e9e9e9",
                                            borderRadius: 8,
                                            background: "none",
                                            cursor: "pointer",
                                            fontSize: 14,
                                            color: "#000",
                                            width: "100%",
                                        }}
                                    >
                                        <span>Skip, buy one way trip</span>
                                        <div
                                            style={{
                                                marginLeft: "auto",
                                                color: "#fb6900",
                                            }}
                                        >
                                            <RightArrow />
                                        </div>
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AeroFlightResults;
